import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useLocation as useLocationHook } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, MapPin, Save, AlertCircle } from "lucide-react";
import { Restaurant, Cuisine } from "@/lib/types";
import { queryClient, safeParseResponse } from "@/lib/queryClient";
import { queryKeys, invalidateAfterRestaurantMutation } from "@/lib/queryKeys";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import RestaurantMap from "@/components/RestaurantMap";
import { MultiSelect, Option } from "@/components/ui/multi-select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export interface RestaurantFormGeneralRef {
  save: () => Promise<void>;
}

interface RestaurantFormGeneralProps {
  restaurant: Restaurant;
  onDirtyChange: (isDirty: boolean) => void;
  onSavingChange: (isSaving: boolean) => void;
}

const RestaurantFormGeneral = forwardRef<RestaurantFormGeneralRef, RestaurantFormGeneralProps>(
  ({ restaurant, onDirtyChange, onSavingChange }, ref) => {
  const [, navigate] = useLocationHook();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);
  
  // Initialform mit den Restaurant-Daten
  const [formData, setFormData] = useState({
    name: restaurant?.name || "",
    slug: restaurant?.slug || "",
    categoryIds: restaurant?.categories?.map(c => c.id.toString()) || [],
    priceRange: restaurant?.priceRange || 2,
    description: restaurant?.description || "",
    street: restaurant?.street || "",
    houseNumber: restaurant?.houseNumber || "",
    postalCode: restaurant?.postalCode || "",
    city: restaurant?.city || "",
    country: restaurant?.country || "Deutschland",
    state: restaurant?.state || "",
    // P0 FIX: phone from phoneNumbers array (first entry)
    phone: restaurant?.phoneNumbers?.[0]?.number || restaurant?.phone || "",
    email: restaurant?.email || "",
    website: restaurant?.website || "",
    facebook: restaurant?.facebook || "",
    instagram: restaurant?.instagram || "",
    latitude: restaurant?.latitude || 52.52,
    longitude: restaurant?.longitude || 13.41,
    enabled: restaurant?.enabled ?? true
  });

  // Aktualisiere formData wenn sich das Restaurant ändert
  useEffect(() => {
    if (restaurant) {
      setFormData(prev => ({
        ...prev,
        name: restaurant.name || "",
        slug: restaurant.slug || "",
        categoryIds: restaurant.categories?.map(c => c.id.toString()) || [],
        priceRange: restaurant.priceRange || 2,
        description: restaurant.description || "",
        street: restaurant.street || "",
        houseNumber: restaurant.houseNumber || "",
        postalCode: restaurant.postalCode || "",
        city: restaurant.city || "",
        country: restaurant.country || "Deutschland",
        state: restaurant.state || "",
        // P0 FIX: phone from phoneNumbers array (first entry)
        phone: restaurant.phoneNumbers?.[0]?.number || restaurant.phone || "",
        email: restaurant.email || "",
        website: restaurant.website || "",
        facebook: restaurant.facebook || "",
        instagram: restaurant.instagram || "",
        latitude: restaurant.latitude || 52.52,
        longitude: restaurant.longitude || 13.41,
        enabled: restaurant.enabled ?? true
      }));
    }
  }, [restaurant]);

  // Fetch cuisines for the dropdown
  const { data: cuisines = [] } = useQuery<Cuisine[]>({
    queryKey: queryKeys.cuisines.list(),
    queryFn: async () => {
      const response = await fetch('/api/cuisines');
      if (!response.ok) throw new Error('Failed to fetch cuisines');
      return response.json();
    },
    staleTime: 60 * 60 * 1000, // 1 Stunde Cache für Kategorien
  });

  // Konvertiere Kategorien in das Format für den MultiSelect
  const cuisineOptions: Option[] = cuisines.map(cuisine => ({
    value: cuisine.id.toString(),
    label: cuisine.name
  }));

  // Debug: Log verfügbare Optionen und aktuelle Auswahl
  React.useEffect(() => {
    if (cuisineOptions.length > 0) {
      console.log("✅ CuisineOptions geladen:", cuisineOptions.length, "verfügbar");
      console.log("🎯 Aktuell ausgewählte categoryIds:", formData.categoryIds);
      console.log("📝 Verfügbare Optionen:", cuisineOptions.map(o => `${o.value}: ${o.label}`));
    }
  }, [cuisineOptions, formData.categoryIds]);

  // P0: Mutation zum Aktualisieren ODER Erstellen des Restaurants
  const updateRestaurantMutation = useMutation({
    mutationFn: async (data: any) => {
      // P0: Wenn id === 'new', erstelle neues Restaurant (POST), sonst Update (PUT)
      const isCreate = restaurant.id === 'new';
      const url = isCreate ? '/api/restaurants' : `/api/restaurants/${restaurant.id}`;
      const method = isCreate ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        // P0: Sichere Error-Parsing
        const errorData = await safeParseResponse<{ error?: string; message?: string }>(response);
        throw new Error(errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`);
      }
      
      // P0: Sichere Response-Parsing
      const result = await safeParseResponse(response);
      if (!result) {
        throw new Error("Leere Antwort vom Server");
      }
      return result;
    },
    onMutate: async (newData) => {
      // Optimistic Update: UI sofort aktualisieren
      await queryClient.cancelQueries({ queryKey: queryKeys.restaurants.detail(restaurant.id) });
      await queryClient.cancelQueries({ queryKey: queryKeys.restaurants.all });
      
      // Aktuelle Daten sichern für Rollback
      const previousRestaurant = queryClient.getQueryData(queryKeys.restaurants.detail(restaurant.id));
      const previousRestaurants = queryClient.getQueryData(queryKeys.restaurants.all);
      
      // Optimistically update einzelnes Restaurant
      queryClient.setQueryData(queryKeys.restaurants.detail(restaurant.id), (old: any) => ({
        ...old,
        ...newData,
        id: restaurant.id // ID beibehalten
      }));
      
      // Optimistically update Restaurant-Listen (falls gecached)
      queryClient.setQueryData(queryKeys.restaurants.all, (old: any[]) => {
        if (!old) return old;
        return old.map(r => r.id === restaurant.id ? { ...r, ...newData } : r);
      });
      
      return { previousRestaurant, previousRestaurants };
    },
    onSuccess: (updatedRestaurant) => {
      // ✅ KORREKTE INVALIDIERUNG: Nutze zentrale Helper-Funktion
      invalidateAfterRestaurantMutation(
        queryClient,
        { id: restaurant.id, slug: updatedRestaurant?.slug },
        { slug: restaurant.slug }
      );
      
      toast({
        title: "Restaurant aktualisiert",
        description: "Die Änderungen wurden erfolgreich gespeichert.",
      });
      
      onDirtyChange(false);
    },
    onError: (error: Error, _newData, context) => {
      // Rollback bei Fehler
      if (context?.previousRestaurant) {
        queryClient.setQueryData(queryKeys.restaurants.detail(restaurant.id), context.previousRestaurant);
      }
      if (context?.previousRestaurants) {
        queryClient.setQueryData(queryKeys.restaurants.all, context.previousRestaurants);
      }
      
      setError(`Fehler beim Speichern: ${error.message}`);
      toast({
        title: "Fehler beim Speichern",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Funktion zur Aktualisierung des isDirty-Status (Prüfen auf Änderungen)
  useEffect(() => {
    const originalCategoryIds = restaurant?.categories?.map(c => c.id.toString()) || [];
    const originalPhone = restaurant?.phoneNumbers?.[0]?.number || restaurant?.phone || "";
    const hasChanges = 
      formData.name !== (restaurant?.name || "") ||
      formData.slug !== (restaurant?.slug || "") ||
      !areArraysEqual(formData.categoryIds, originalCategoryIds) ||
      Number(formData.priceRange) !== (restaurant?.priceRange || 2) ||
      formData.description !== (restaurant?.description || "") ||
      formData.street !== (restaurant?.street || "") ||
      formData.houseNumber !== (restaurant?.houseNumber || "") ||
      formData.postalCode !== (restaurant?.postalCode || "") ||
      formData.city !== (restaurant?.city || "") ||
      formData.country !== (restaurant?.country || "Deutschland") ||
      formData.state !== (restaurant?.state || "") ||
      formData.phone !== originalPhone ||
      formData.email !== (restaurant?.email || "") ||
      formData.website !== (restaurant?.website || "") ||
      formData.facebook !== (restaurant?.facebook || "") ||
      formData.instagram !== (restaurant?.instagram || "") ||
      formData.latitude !== (restaurant?.latitude || 52.52) ||
      formData.longitude !== (restaurant?.longitude || 13.41) ||
      formData.enabled !== (restaurant?.enabled ?? true);
    
    onDirtyChange(hasChanges);
  }, [formData, restaurant, onDirtyChange]);

  // Hilfsfunktion zum Vergleichen von Arrays
  const areArraysEqual = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();
    return sortedArr1.every((val, idx) => val === sortedArr2[idx]);
  };

  // Handler für Input-Änderungen
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handler für Select-Änderungen (für die shadcn/ui Select-Komponente)
  // const handleSelectChange = (name: string, value: string) => {
  //   setFormData(prev => ({ ...prev, [name]: value }));
  // };

  // Handler für Zahlen-Inputs
  const handleNumberChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: Number(value) }));
  };

  // Handler für Kategorie-Auswahl
  const handleCategoryChange = (selected: string[]) => {
    console.log("Kategorie-Änderung:", selected);
    console.log("Aktuelle categoryIds:", formData.categoryIds);
    console.log("Verfügbare cuisineOptions:", cuisineOptions);
    setFormData(prev => ({ ...prev, categoryIds: selected }));
  };

  // Handler für Boolean-Inputs (Switch)
  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  // Koordinaten aus der Adresse abrufen
  const fetchCoordinates = async () => {
    if (!formData.street || !formData.city || !formData.postalCode) {
      toast({
        title: "Adresse unvollständig",
        description: "Bitte geben Sie eine vollständige Adresse ein (Straße, Stadt, PLZ).",
        variant: "destructive"
      });
      return;
    }

    setIsLoadingCoordinates(true);
    try {
      const address = `${formData.street} ${formData.houseNumber}, ${formData.postalCode} ${formData.city}, ${formData.country}`;
      
      // Zuerst den API-Key abrufen
      const configResponse = await fetch('/api/config');
      const config = await safeParseResponse<{ googleMapsApiKey?: string }>(configResponse);
      const apiKey = config?.googleMapsApiKey;
      
      if (!apiKey) {
        throw new Error("Google Maps API-Key konnte nicht abgerufen werden");
      }
      
      // Google Maps Geocoding API verwenden
      const geocodeResponse = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
      );
      
      if (!geocodeResponse.ok) {
        throw new Error(`Fehler beim Abrufen der Koordinaten: ${geocodeResponse.status}`);
      }
      
      const geocodeData = await safeParseResponse(geocodeResponse);
      
      if (geocodeData.status === "OK" && geocodeData.results && geocodeData.results.length > 0) {
        const location = geocodeData.results[0].geometry.location;
        
        setFormData(prev => ({
          ...prev,
          latitude: location.lat,
          longitude: location.lng
        }));
        
        toast({
          title: "Koordinaten aktualisiert",
          description: "Die Koordinaten wurden erfolgreich aktualisiert."
        });
      } else {
        toast({
          title: "Keine Koordinaten gefunden",
          description: "Für die eingegebene Adresse konnten keine Koordinaten gefunden werden.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Bei der Abfrage der Koordinaten ist ein Fehler aufgetreten.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingCoordinates(false);
    }
  };

  // P0: Expose save() function via ref für Mobile Save Bar
  useImperativeHandle(ref, () => ({
    save: async () => {
      onSavingChange(true);
      setError(null);
      try {
        await updateRestaurantMutation.mutateAsync(formData);
      } catch (err) {
        console.error("Fehler beim Aktualisieren des Restaurants:", err);
        throw err;
      } finally {
        onSavingChange(false);
      }
    }
  }));

  // Formular speichern
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onSavingChange(true);
    setError(null);

    try {
      // P0 FRONTEND VALIDATION: Import validation functions
      const { 
        validateURL, 
        validateEmail, 
        validatePriceRange, 
        validateLatitude, 
        validateLongitude 
      } = await import("@shared/validation");
      
      const validationErrors: string[] = [];
      
      // Name ist required
      if (!formData.name || formData.name.trim() === "") {
        validationErrors.push("Restaurantname ist erforderlich");
      }
      
      // URLs validieren
      if (formData.website && formData.website.trim() !== "") {
        const validWebsite = validateURL(formData.website);
        if (validWebsite === null) {
          validationErrors.push("Website-URL ist ungültig");
        }
      }
      
      if (formData.facebook && formData.facebook.trim() !== "") {
        const validFacebook = validateURL(formData.facebook);
        if (validFacebook === null) {
          validationErrors.push("Facebook-URL ist ungültig");
        }
      }
      
      if (formData.instagram && formData.instagram.trim() !== "") {
        const validInstagram = validateURL(formData.instagram);
        if (validInstagram === null) {
          validationErrors.push("Instagram-URL ist ungültig");
        }
      }
      
      // Email validieren
      if (formData.email && formData.email.trim() !== "") {
        if (!validateEmail(formData.email)) {
          validationErrors.push("E-Mail-Adresse ist ungültig");
        }
      }
      
      // Price Range validieren
      const priceValidation = validatePriceRange(formData.priceRange);
      if (!priceValidation.valid) {
        validationErrors.push("Preis-Range muss zwischen 1 und 4 liegen");
      }
      
      // Geo-Koordinaten validieren
      const latValidation = validateLatitude(formData.latitude);
      if (!latValidation.valid) {
        validationErrors.push(latValidation.error || "Latitude ungültig");
      }
      
      const lngValidation = validateLongitude(formData.longitude);
      if (!lngValidation.valid) {
        validationErrors.push(lngValidation.error || "Longitude ungültig");
      }
      
      // Wenn Fehler, zeige sie an und stoppe
      if (validationErrors.length > 0) {
        setError(validationErrors.join(", "));
        toast({
          title: "Validierungsfehler",
          description: validationErrors[0], // Zeige ersten Fehler
          variant: "destructive"
        });
        onSavingChange(false);
        return;
      }
      
      await updateRestaurantMutation.mutateAsync(formData);
    } catch (err) {
      console.error("Fehler beim Aktualisieren des Restaurants:", err);
    } finally {
      onSavingChange(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-full">
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* Allgemeine Informationen */}
        <Card>
          <CardHeader>
            <CardTitle>Allgemeine Informationen</CardTitle>
            <CardDescription>Grundlegende Informationen über das Restaurant</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div className="col-span-1 md:col-span-2">
                <Label htmlFor="name" className="font-medium">Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Restaurant-Name"
                  required
                  className="mt-1"
                />
              </div>

              {/* Slug */}
              <div className="col-span-1 md:col-span-2">
                <Label htmlFor="slug" className="font-medium">Slug (URL-freundlicher Name) <span className="text-red-500">*</span></Label>
                <Input
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="restaurant-name"
                  required
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">Wird in der URL verwendet: speiseguru.de/restaurants/<span className="font-mono">{formData.slug || 'restaurant-name'}</span></p>
              </div>

              {/* Küche / Kategorien */}
              <div className="col-span-1 md:col-span-2">
                <Label htmlFor="categories" className="font-medium">Küche / Kategorien <span className="text-red-500">*</span></Label>
                <div className="mt-1">
                  <MultiSelect
                    options={cuisineOptions}
                    selected={formData.categoryIds}
                    onChange={handleCategoryChange}
                    placeholder="Küchen auswählen"
                    disabled={updateRestaurantMutation.isPending}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">Sie können mehrere Küchen/Kategorien auswählen</p>
              </div>

              {/* Preisklasse */}
              <div>
                <Label htmlFor="priceRange" className="font-medium">Preisklasse</Label>
                <Select
                  value={String(formData.priceRange)}
                  onValueChange={(value) => handleNumberChange("priceRange", value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Preisklasse wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Günstig</SelectItem>
                    <SelectItem value="2">Mittel</SelectItem>
                    <SelectItem value="3">Gehoben</SelectItem>
                    <SelectItem value="4">Luxuriös</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Über uns / Beschreibung */}
              <div className="col-span-1 md:col-span-2">
                <Label htmlFor="description" className="font-medium">Über uns</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Beschreibe dein Restaurant, Team, Geschichte oder Besonderheiten (wird im Reiter „Über uns“ angezeigt)"
                  className="mt-1 h-24"
                />
              </div>
            </div>
            
            {/* Aktivierungsstatus */}
            <div className="flex items-center space-x-2 mt-4 pt-4 border-t">
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => handleSwitchChange("enabled", checked)}
              />
              <Label htmlFor="enabled" className="font-medium">
                Restaurant aktiv
              </Label>
              <p className="text-sm text-gray-500 ml-2">
                {formData.enabled ? 'Das Restaurant ist für Kunden sichtbar' : 'Das Restaurant ist für Kunden nicht sichtbar'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Adresse */}
        <Card>
          <CardHeader>
            <CardTitle>Adresse</CardTitle>
            <CardDescription>Standort des Restaurants</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {/* Straße */}
              <div className="col-span-1 md:col-span-4">
                <Label htmlFor="street" className="font-medium">Straße <span className="text-red-500">*</span></Label>
                <Input
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  placeholder="Straße"
                  required
                  className="mt-1"
                />
              </div>

              {/* Hausnummer */}
              <div className="col-span-1 md:col-span-2">
                <Label htmlFor="houseNumber" className="font-medium">Hausnummer</Label>
                <Input
                  id="houseNumber"
                  name="houseNumber"
                  value={formData.houseNumber}
                  onChange={handleInputChange}
                  placeholder="Nr."
                  className="mt-1"
                />
              </div>

              {/* PLZ */}
              <div className="col-span-1 md:col-span-2">
                <Label htmlFor="postalCode" className="font-medium">PLZ <span className="text-red-500">*</span></Label>
                <Input
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  placeholder="Postleitzahl"
                  required
                  className="mt-1"
                />
              </div>

              {/* Stadt */}
              <div className="col-span-1 md:col-span-4">
                <Label htmlFor="city" className="font-medium">Stadt <span className="text-red-500">*</span></Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Stadt"
                  required
                  className="mt-1"
                />
              </div>

              {/* Land */}
              <div className="col-span-1 md:col-span-3">
                <Label htmlFor="country" className="font-medium">Land</Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Land"
                  className="mt-1"
                />
              </div>

              {/* Bundesland */}
              <div className="col-span-1 md:col-span-3">
                <Label htmlFor="state" className="font-medium">Bundesland</Label>
                <Input
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Bundesland"
                  className="mt-1"
                />
              </div>
            </div>
            
            {/* Koordinaten-Bereich */}
            {/* Desktop: Immer sichtbar */}
            <div className="hidden md:block mt-6 border-t pt-4">
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
                <h3 className="text-sm font-medium">Geokoordinaten</h3>
                <Button 
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={fetchCoordinates}
                  disabled={isLoadingCoordinates}
                  className="ml-auto text-xs sm:text-sm overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {isLoadingCoordinates ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      <span>Koordinaten werden ermittelt...</span>
                    </>
                  ) : (
                    <>
                      <MapPin className="mr-1 h-4 w-4" />
                      <span>Koordinaten automatisch ermitteln</span>
                    </>
                  )}
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude" className="font-medium">Breitengrad</Label>
                  <Input
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="0.000001"
                    value={formData.latitude}
                    onChange={(e) => handleNumberChange("latitude", e.target.value)}
                    placeholder="z.B. 52.520008"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude" className="font-medium">Längengrad</Label>
                  <Input
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="0.000001"
                    value={formData.longitude}
                    onChange={(e) => handleNumberChange("longitude", e.target.value)}
                    placeholder="z.B. 13.404954"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="mt-4 border rounded-lg overflow-hidden h-[200px]">
                {formData.latitude && formData.longitude ? (
                  <RestaurantMap 
                    lat={formData.latitude} 
                    lng={formData.longitude} 
                    name={formData.name || "Restaurant"}
                    zoom={14}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center bg-gray-100">
                    <div className="text-center text-gray-500">
                      <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p>Kartenvorschau wird hier angezeigt</p>
                      <p className="text-xs">Koordinaten: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile: Collapsible Accordion (default closed) */}
            <div className="md:hidden mt-6 border-t pt-4">
              <Accordion type="single" collapsible>
                <AccordionItem value="coordinates" className="border-none">
                  <AccordionTrigger className="py-2">
                    <div className="flex items-center justify-between w-full pr-2">
                      <span className="text-sm font-medium">Geokoordinaten</span>
                      <Button 
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchCoordinates();
                        }}
                        disabled={isLoadingCoordinates}
                        className="h-8 px-2"
                      >
                        {isLoadingCoordinates ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MapPin className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="latitude-mobile" className="font-medium">Breitengrad</Label>
                        <Input
                          id="latitude-mobile"
                          name="latitude"
                          type="number"
                          step="0.000001"
                          value={formData.latitude}
                          onChange={(e) => handleNumberChange("latitude", e.target.value)}
                          placeholder="z.B. 52.520008"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="longitude-mobile" className="font-medium">Längengrad</Label>
                        <Input
                          id="longitude-mobile"
                          name="longitude"
                          type="number"
                          step="0.000001"
                          value={formData.longitude}
                          onChange={(e) => handleNumberChange("longitude", e.target.value)}
                          placeholder="z.B. 13.404954"
                          className="mt-1"
                        />
                      </div>
                      <div className="border rounded-lg overflow-hidden h-[200px]">
                        {formData.latitude && formData.longitude ? (
                          <RestaurantMap 
                            lat={formData.latitude} 
                            lng={formData.longitude} 
                            name={formData.name || "Restaurant"}
                            zoom={14}
                          />
                        ) : (
                          <div className="h-full flex items-center justify-center bg-gray-100">
                            <div className="text-center text-gray-500">
                              <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                              <p className="text-xs">Kartenvorschau</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </CardContent>
        </Card>

        {/* Kontakt */}
        <Card>
          <CardHeader>
            <CardTitle>Kontaktinformationen</CardTitle>
            <CardDescription>Kontaktdaten des Restaurants</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Telefon */}
              <div>
                <Label htmlFor="phone" className="font-medium">Telefon</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Telefonnummer"
                  className="mt-1"
                />
              </div>

              {/* E-Mail */}
              <div>
                <Label htmlFor="email" className="font-medium">E-Mail</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="E-Mail-Adresse"
                  className="mt-1"
                />
              </div>

              {/* Website */}
              <div>
                <Label htmlFor="website" className="font-medium">Website</Label>
                <Input
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://www.example.com"
                  className="mt-1"
                />
              </div>

              {/* Facebook */}
              <div>
                <Label htmlFor="facebook" className="font-medium">Facebook</Label>
                <Input
                  id="facebook"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleInputChange}
                  placeholder="z.B. https://www.facebook.com/restaurantname"
                  className="mt-1"
                />
              </div>

              {/* Instagram */}
              <div>
                <Label htmlFor="instagram" className="font-medium">Instagram</Label>
                <Input
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleInputChange}
                  placeholder="z.B. https://www.instagram.com/restaurantname"
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Desktop save bar — mobile save is handled by RestaurantEditPage bottom bar */}
        <div className="hidden md:flex justify-end gap-3 mt-8 pt-4 border-t border-slate-200/60">
          <Button 
            type="button" 
            variant="ghost" 
            onClick={() => navigate("/admin/restaurants")}
            className="text-slate-600"
          >
            Abbrechen
          </Button>
          <Button 
            type="submit" 
            disabled={updateRestaurantMutation.isPending} 
            className="bg-primary shadow-sm shadow-primary/20 min-w-[120px]"
          >
            {updateRestaurantMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Speichern…
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Speichern
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  );
});

RestaurantFormGeneral.displayName = "RestaurantFormGeneral";

export default RestaurantFormGeneral; 